import { useState, useEffect, useRef } from 'react';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { Warning3Icon } from '@qitech/icons';

import { PrivatePayrollAuctionLoading } from '../Loading';
import { ErrorPage } from '../../../ErrorPage';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { cpfMask } from '../../../../utils/utils';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { ThemedInput } from '../../CreditOperationAndSocialSecurityCards/components/ThemedInput';
import * as S from './styles';

interface EnrichPersonalDataProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

type SubStep = 'personal_data' | 'address' | 'payment_data';
type PaymentMethod = 'pix_key' | 'account';
type PixKeyType = 'cpf' | 'phone' | 'email' | 'random_key';

const SIGNED_STATUSES = ['signed', 'sending_sign_receipt', 'receipt_sent', 'analyzing', 'analyzed', 'completed'];

const BR_STATES = [
    'AC','AL','AM','AP','BA','CE','DF','ES','GO',
    'MA','MG','MS','MT','PA','PB','PE','PI','PR',
    'RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

const STATE_NAMES: Record<string, string> = {
    AC:'Acre',AL:'Alagoas',AM:'Amazonas',AP:'Amapá',BA:'Bahia',CE:'Ceará',
    DF:'Distrito Federal',ES:'Espírito Santo',GO:'Goiás',MA:'Maranhão',
    MG:'Minas Gerais',MS:'Mato Grosso do Sul',MT:'Mato Grosso',PA:'Pará',
    PB:'Paraíba',PE:'Pernambuco',PI:'Piauí',PR:'Paraná',RJ:'Rio de Janeiro',
    RN:'Rio Grande do Norte',RO:'Rondônia',RR:'Roraima',RS:'Rio Grande do Sul',
    SC:'Santa Catarina',SE:'Sergipe',SP:'São Paulo',TO:'Tocantins',
};

const phoneMask = (value: string) => {
    const d = value.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return d.length ? `(${d}` : '';
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const sanitizePhone = (masked: string) => masked.replace(/\D/g, '');

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const STEPS: { key: SubStep; label: string; index: number }[] = [
    { key: 'personal_data', label: 'Dados',     index: 1 },
    { key: 'address',       label: 'Endereço',  index: 2 },
    { key: 'payment_data',  label: 'Conta para crédito', index: 3 },
];

export const PrivatePayrollAuctionEnrichPersonalData = ({
    signData,
    setSignerStep,
    setSignData,
}: EnrichPersonalDataProps) => {
    const { clientThemeSettings } = useThemeContext();
    const { signerHash } = useParams<{ signerHash: string }>();

    const primary = resolveColor('primary', clientThemeSettings);
    const { contentEndRef, hasScrolledToEnd, scrollToBottom, reset: resetScroll } = useScrollToBottom([]);

    // ── Loading / error ─────────────────────────────
    const [loading, setLoading] = useState(false);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [error, setError] = useState<{ has_error: boolean; status: number }>({
        has_error: false,
        status: 0,
    });

    // ── Sub-step ────────────────────────────────────
    const [currentStep, setCurrentStep] = useState<SubStep>('personal_data');
    const currentStepRef = useRef<SubStep>('personal_data');

    useEffect(() => {
        currentStepRef.current = currentStep;
        resetScroll();
    }, [currentStep, resetScroll]);

    useEffect(() => {
        window.history.pushState({ enrichStep: 'personal_data' }, '');

        const handlePopState = (event: PopStateEvent) => {
            const enrichStep = event.state?.enrichStep as SubStep | undefined;
            if (enrichStep && STEPS.some((s) => s.key === enrichStep)) {
                setCurrentStep(enrichStep);
            } else {
                window.history.pushState({ enrichStep: currentStepRef.current }, '');
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Step 1 – personal_data ───────────────────────
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    // ── Step 2 – address ────────────────────────────
    const [postalCodeLoading, setPostalCodeLoading] = useState(false);
    const addressNumberRef = useRef<HTMLDivElement>(null);
    const [address, setAddress] = useState<any>(
        signData?.signer_data?.address ?? {
            street: '', number: '', complement: '',
            neighborhood: '', city: '', uf: 'SP', postal_code: '',
        },
    );

    // ── Step 3 – payment_data ───────────────────────
    const isForcedPix = signData?.forced_payment_method === 'pix_document_number';
    const isForcedPixOrAccount = signData?.forced_payment_method === 'pix_document_number_or_account';
    const documentNumber = signData?.signer_data?.document_number?.replace(/\D/g, '') ?? '';

    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pix_key');
    const [pixKeyType, setPixKeyType] = useState<PixKeyType>('cpf');
    const [pixKey, setPixKey] = useState(isForcedPix ? documentNumber : '');
    const [bankAccount, setBankAccount] = useState('');
    const [bankAccountDigit, setBankAccountDigit] = useState('');
    const [bankAgency, setBankAgency] = useState('');
    const [bankCode, setBankCode] = useState('');

    useEffect(() => {
        if (pixKeyType === 'cpf') {
            setPixKey(signData.signer_data.document_number);
        }
    }, [pixKeyType, signData])

    // ── Validation helpers ───────────────────────────
    const isStep1Valid =
        sanitizePhone(phone).length === 11 &&
        emailRegex.test(email);

    const isAddressValid = () =>
        address.street.length > 0 &&
        address.number.length > 0 &&
        address.neighborhood.length > 0 &&
        address.city.length > 0 &&
        address.uf.length > 0 &&
        address.postal_code.length === 9;

    const isPixValid = () => {
        if (pixKeyType === 'cpf') return pixKey.replace(/\D/g, '').length === 11;
        if (pixKeyType === 'phone') return sanitizePhone(pixKey).length === 11;
        if (pixKeyType === 'email') return emailRegex.test(pixKey);
        if (pixKeyType === 'random_key') return pixKey.length === 36;
        return false;
    };

    const isBankAccountValid = () =>
        bankAccount.length >= 1 &&
        bankAccountDigit.length >= 1 &&
        bankAgency.length === 4 &&
        bankCode.length === 3;

    const isStep3Valid = () => {
        if (isForcedPix) return documentNumber.length === 11;
        if (paymentMethod === 'account') return isBankAccountValid();
        if (isForcedPixOrAccount) return documentNumber.length === 11;
        return isPixValid();
    };

    // ── Address utilities ────────────────────────────
    const formatCEP = (v: string) => {
        const d = v.replace(/\D/g, '');
        return d.replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
    };

    const handlePostalCodeSearch = async (postalCode: string) => {
        setPostalCodeLoading(true);
        try {
            const config = { headers: { Authorization: signData.signer_token } };
            const { data } = await axios.get(`/signer/postal_code?postal_code=${postalCode}`, config);
            setAddress((prev: any) => ({
                ...prev,
                postal_code: postalCode,
                street: data.address ?? '',
                neighborhood: data.neighborhood ?? '',
                city: data.city ?? '',
                uf: data.short_state_name ?? '',
            }));
        } catch (err) {
            console.warn(err);
        } finally {
            setPostalCodeLoading(false);
            setTimeout(() => {
                addressNumberRef.current?.querySelector('input')?.focus();
            }, 0);
        }
    };

    // ── PIX key masking by type ──────────────────────
    const maskedPixKey = () => {
        if (pixKeyType === 'cpf')   return cpfMask(pixKey);
        if (pixKeyType === 'phone') return phoneMask(pixKey);
        return pixKey;
    };

    const handlePixKeyChange = (raw: string) => {
        if (pixKeyType === 'cpf')   setPixKey(raw.replace(/\D/g, '').slice(0, 11));
        else if (pixKeyType === 'phone') setPixKey(raw.replace(/\D/g, '').slice(0, 11));
        else setPixKey(raw);
    };

    // ── Submit ───────────────────────────────────────
    const handleSubmit = async () => {
        if (hasMadeRequest || !isStep3Valid()) return;

        let browserKey: string | null = null;
        try { browserKey = await getOrCreateBrowserKey(); } catch (_) {}

        const phoneDigits = sanitizePhone(phone);
        const formattedPixKey = pixKeyType === 'phone' ? `+55${sanitizePhone(pixKey)}` : pixKey;

        const paymentData = isForcedPix
            ? { pix_key: documentNumber }
            : paymentMethod === 'account'
            ? {
                account_data: {
                    bank_code: bankCode,
                    branch_number: bankAgency,
                    account_number: bankAccount,
                    account_digit: bankAccountDigit,
                },
            }
            : isForcedPixOrAccount
            ? { pix_key: documentNumber }
            : {
                pix_key: pixKeyType === 'cpf' ? documentNumber : formattedPixKey,
            };

        const payload = {
            event_date: new Date().toISOString(),
            step: 'enrich_personal_data',
            phone: {
                international_dial_code: '55',
                area_code: phoneDigits.slice(0, 2),
                number: phoneDigits.slice(2),
            },
            email,
            ...paymentData,
            address: {
                street: address.street.trim(),
                number: address.number.trim(),
                complement: address.complement ? address.complement.trim() : undefined,
                neighborhood: address.neighborhood.trim(),
                city: address.city.trim(),
                uf: address.uf.trim(),
                postal_code: address.postal_code.trim(),
            },
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        try {
            setLoading(true);
            setHasMadeRequest(true);
            const response = await axios.patch('/signer', payload, config);
            setSignData({
                ...signData,
                ...response.data,
                signer_data: {
                    ...signData.signer_data,
                    ...(response.data.signer_data ?? {}),
                    email,
                    phone: {
                        international_dial_code: '55',
                        area_code: phoneDigits.slice(0, 2),
                        number: phoneDigits.slice(2),
                    },
                },
            });

            if (response.data.next_step) {
                setSignerStep(response.data.next_step);
            } else {
                const getResponse = await axios.get(`/signer/hash/${signerHash}`);
                const status = getResponse.data?.status;
                if (SIGNED_STATUSES.includes(status)) {
                    setSignerStep('signed');
                } else if (getResponse.data?.next_step) {
                    setSignerStep(getResponse.data.next_step);
                }
            }
        } catch (err: any) {
            const status = [401, 403, 409].includes((err?.response || {}).status)
                ? err.response.status
                : 500;
            setError({ has_error: true, status });
            setHasMadeRequest(false);
        } finally {
            setLoading(false);
        }
    };

    // ── Payment confirmation sheet ───────────────────
    const confirmModal = useAnimatedModal();

    // Level 1: navigate back in the browser history stack by the exact delta so
    // the history pointer lands on the target entry. The popstate handler then
    // calls setCurrentStep. Using history.go(-delta) instead of setCurrentStep
    // directly keeps the stack linear: when the user presses "Continuar" after
    // jumping back, pushState replaces the orphaned future entries automatically.
    const goToStep = (targetStep: SubStep) => {
        const currentIndex = STEPS.findIndex((s) => s.key === currentStepRef.current);
        const targetIndex  = STEPS.findIndex((s) => s.key === targetStep);
        const delta = currentIndex - targetIndex;
        if (delta > 0) window.history.go(-delta);
    };

    const handleContinue = () => {
        if (currentStep === 'personal_data') {
            window.history.pushState({ enrichStep: 'address' }, '');
            setCurrentStep('address');
        } else if (currentStep === 'address') {
            window.history.pushState({ enrichStep: 'payment_data' }, '');
            setCurrentStep('payment_data');
        } else {
            confirmModal.open();
        }
    };

    const isContinueDisabled = () => {
        if (currentStep === 'personal_data') return !isStep1Valid;
        if (currentStep === 'address')       return !isAddressValid();
        return !isStep3Valid();
    };

    // ── Guards ───────────────────────────────────────
    if (loading) return <PrivatePayrollAuctionLoading />;

    if (error.has_error) {
        return (
            <ErrorPage
                error={error}
                redirectToLogin={() => setSignerStep('login')}
                resetError={() => setError({ has_error: false, status: 0 })}
                clientThemeSettings={clientThemeSettings}
            />
        );
    }

    // ── Payment data display helpers ─────────────────
    const pixTypeLabel: Record<string, string> = {
        cpf: 'CPF', phone: 'Celular', email: 'E-mail', random_key: 'Chave aleatória',
    };

    // ── Step indicator ───────────────────────────────
    const activeIndex = STEPS.findIndex((s) => s.key === currentStep);
    const stepsBefore = STEPS.slice(0, activeIndex);
    const activeStep  = STEPS[activeIndex];
    const stepsAfter  = STEPS.slice(activeIndex + 1);

    const stepIndicator = (
        <S.StepIndicatorContainer>
            <S.StepRow>
                <S.StepGroup>
                    {stepsBefore.map((step) => (
                        <S.StepCircle
                            key={step.key}
                            $active={true}
                            $color={primary}
                            $clickable
                            onClick={() => goToStep(step.key)}
                            title={step.label}
                        >
                            {step.index}
                        </S.StepCircle>
                    ))}
                    <S.StepActiveItem>
                        <S.StepCircle $active $color={primary}>
                            {activeStep.index}
                        </S.StepCircle>
                        <S.StepLabel $color={primary}>{activeStep.label}</S.StepLabel>
                    </S.StepActiveItem>
                </S.StepGroup>

                {stepsAfter.length > 0 && (
                    <S.StepGroup>
                        {stepsAfter.map((step) => (
                            <S.StepCircle key={step.key} $active={false} $color={primary}>
                                {step.index}
                            </S.StepCircle>
                        ))}
                    </S.StepGroup>
                )}
            </S.StepRow>
        </S.StepIndicatorContainer>
    );

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent>
                <S.HeroSection>
                    <S.Title $color={primary}>Insira seus dados</S.Title>
                    <S.Subtitle>Preencha os campos abaixo e clique em continuar.</S.Subtitle>
                </S.HeroSection>

                {stepIndicator}

                {/* ── Step 1: Dados ──────────────────────── */}
                {currentStep === 'personal_data' && (
                    <>
                        <S.FormSection>
                            <ThemedInput
                                labelValue="Celular"
                                labelColor="#666"
                                fieldValue={phoneMask(phone)}
                                hasError={false}
                                errorMessage=""
                                type="numeric"
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Email"
                                labelColor="#666"
                                fieldValue={email}
                                hasError={false}
                                errorMessage=""
                                type="email"
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => {}}
                            />
                        </S.FormSection>
                    </>
                )}

                {/* ── Step 2: Endereço ───────────────────── */}
                {currentStep === 'address' && (
                    <>
                        <S.FormSection style={{ marginBottom: '16px'}}>
                            <ThemedInput
                                labelValue="CEP"
                                labelColor="#666"
                                fieldValue={address.postal_code}
                                hasError={false}
                                errorMessage=""
                                type="numeric"
                                onChange={(e) => {
                                    const formatted = formatCEP(e.target.value);
                                    setAddress((prev: any) => ({ ...prev, postal_code: formatted }));
                                    if (/^\d{5}-\d{3}$/.test(formatted) && !postalCodeLoading) {
                                        handlePostalCodeSearch(formatted);
                                    }
                                }}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Cidade"
                                labelColor="#666"
                                fieldValue={address.city}
                                hasError={false}
                                errorMessage=""
                                type="text"
                                onChange={(e) => setAddress((prev: any) => ({ ...prev, city: e.target.value }))}
                                onBlur={() => {}}
                            />
                            <S.SelectGroup>
                                <S.SelectLabel>Estado</S.SelectLabel>
                                <S.StyledSelect
                                    value={address.uf}
                                    onChange={(e) =>
                                        setAddress((prev: any) => ({ ...prev, uf: e.target.value }))
                                    }
                                >
                                    {BR_STATES.map((uf) => (
                                        <option key={uf} value={uf}>{STATE_NAMES[uf]} ({uf})</option>
                                    ))}
                                </S.StyledSelect>
                            </S.SelectGroup>
                            <ThemedInput
                                labelValue="Bairro"
                                labelColor="#666"
                                fieldValue={address.neighborhood}
                                hasError={false}
                                errorMessage=""
                                type="text"
                                onChange={(e) => setAddress((prev: any) => ({ ...prev, neighborhood: e.target.value }))}
                                onBlur={() => {}}
                            />
                            <ThemedInput
                                labelValue="Endereço"
                                labelColor="#666"
                                fieldValue={address.street}
                                hasError={false}
                                errorMessage=""
                                type="text"
                                onChange={(e) => setAddress((prev: any) => ({ ...prev, street: e.target.value }))}
                                onBlur={() => {}}
                            />
                            <div ref={addressNumberRef}>
                                <ThemedInput
                                    labelValue="Número"
                                    labelColor="#666"
                                    fieldValue={address.number}
                                    hasError={false}
                                    errorMessage=""
                                    type="numeric"
                                    onChange={(e) =>
                                        setAddress((prev: any) => ({
                                            ...prev,
                                            number: e.target.value.replace(/\D/g, '').slice(0, 5),
                                        }))
                                    }
                                    onBlur={() => {}}
                                />
                            </div>
                            <ThemedInput
                                labelValue="Complemento"
                                labelColor="#666"
                                fieldValue={address.complement || ''}
                                hasError={false}
                                errorMessage=""
                                type="text"
                                onChange={(e) => setAddress((prev: any) => ({ ...prev, complement: e.target.value }))}
                                onBlur={() => {}}
                            />
                        </S.FormSection>
                    </>
                )}

                {/* ── Step 3: Pagamento ──────────────────── */}
                {currentStep === 'payment_data' && (
                    <>
                        <S.FormSection>
                            {isForcedPix ? (
                                <>
                                    <S.ConfirmRow>
                                        <S.ConfirmLabel>Chave PIX (CPF)</S.ConfirmLabel>
                                        <S.ConfirmValue>{cpfMask(documentNumber)}</S.ConfirmValue>
                                    </S.ConfirmRow>
                                    <S.Callout>
                                        <S.CalloutIcon><Warning3Icon size={16} /></S.CalloutIcon>
                                        <S.CalloutText>
                                            Lembre-se de verificar se você tem uma chave PIX cadastrada no seu CPF.
                                        </S.CalloutText>
                                    </S.Callout>
                                </>
                            ) : (
                                <>
                                    <S.PaymentToggle>
                                        <S.ToggleButton
                                            $active={paymentMethod === 'pix_key'}
                                            $color={primary}
                                            onClick={() => setPaymentMethod('pix_key')}
                                        >
                                            Chave PIX
                                        </S.ToggleButton>
                                        <S.ToggleButton
                                            $active={paymentMethod === 'account'}
                                            $color={primary}
                                            onClick={() => setPaymentMethod('account')}
                                        >
                                            Conta bancária
                                        </S.ToggleButton>
                                    </S.PaymentToggle>

                                    {paymentMethod === 'pix_key' && (
                                        isForcedPixOrAccount ? (
                                            <>
                                                <S.ConfirmRow>
                                                    <S.ConfirmLabel>Chave PIX (CPF)</S.ConfirmLabel>
                                                    <S.ConfirmValue>{cpfMask(documentNumber)}</S.ConfirmValue>
                                                </S.ConfirmRow>
                                                <S.Callout>
                                                    <S.CalloutIcon><Warning3Icon size={16} /></S.CalloutIcon>
                                                    <S.CalloutText>
                                                        Lembre-se de verificar se você tem uma chave PIX cadastrada no seu CPF.
                                                    </S.CalloutText>
                                                </S.Callout>
                                            </>
                                        ) : (
                                        <>
                                            <S.SelectGroup>
                                                <S.SelectLabel>Tipo de chave PIX</S.SelectLabel>
                                                <S.StyledSelect
                                                    value={pixKeyType}
                                                    onChange={(e) => {
                                                        setPixKeyType(e.target.value as PixKeyType);
                                                        setPixKey('');
                                                    }}
                                                >
                                                    <option value="cpf">CPF</option>
                                                    <option value="phone">Celular</option>
                                                    <option value="email">E-mail</option>
                                                    <option value="random_key">Chave aleatória</option>
                                                </S.StyledSelect>
                                            </S.SelectGroup>
                                            <ThemedInput
                                                labelValue="Chave PIX"
                                                labelColor="#666"
                                                fieldValue={pixKeyType === 'cpf' ? signData.signer_data.document_number : pixKeyType === 'phone' ? phoneMask(pixKey) : pixKey}
                                                hasError={false}
                                                errorMessage=""
                                                type={pixKeyType === 'cpf' || pixKeyType === 'phone' ? 'numeric' : pixKeyType === 'email' ? 'email' : 'text'}
                                                onChange={(e) => pixKeyType !== 'cpf' && handlePixKeyChange(e.target.value)}
                                                disabled={pixKeyType === 'cpf'}
                                                onBlur={() => {}}
                                            />
                                            <S.Callout>
                                                <S.CalloutIcon><Warning3Icon size={16} /></S.CalloutIcon>
                                                <S.CalloutText>
                                                    Lembre-se de verificar se você tem uma chave PIX cadastrada no seu CPF.
                                                </S.CalloutText>
                                            </S.Callout>
                                        </>
                                        )
                                    )}

                                    {paymentMethod === 'account' && (
                                        <>
                                            <ThemedInput
                                                labelValue="Código do Banco"
                                                labelColor="#666"
                                                fieldValue={bankCode}
                                                hasError={false}
                                                errorMessage=""
                                                type="numeric"
                                                onChange={(e) =>
                                                    setBankCode(e.target.value.replace(/\D/g, '').slice(0, 3))
                                                }
                                                onBlur={() => {}}
                                            />
                                            <ThemedInput
                                                labelValue="Agência"
                                                labelColor="#666"
                                                fieldValue={bankAgency}
                                                hasError={false}
                                                errorMessage=""
                                                type="numeric"
                                                onChange={(e) =>
                                                    setBankAgency(e.target.value.replace(/\D/g, '').slice(0, 4))
                                                }
                                                onBlur={() => {}}
                                            />
                                            <S.AccountRow>
                                                <S.AccountNumberWrapper>
                                                    <ThemedInput
                                                        labelValue="Conta"
                                                        labelColor="#666"
                                                        fieldValue={bankAccount}
                                                        hasError={false}
                                                        errorMessage=""
                                                        type="numeric"
                                                        onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, ''))}
                                                        onBlur={() => {}}
                                                    />
                                                </S.AccountNumberWrapper>
                                                <S.AccountSeparator>-</S.AccountSeparator>
                                                <S.AccountDigitWrapper>
                                                    <ThemedInput
                                                        labelValue="Dígito"
                                                        labelColor="#666"
                                                        fieldValue={bankAccountDigit}
                                                        hasError={false}
                                                        errorMessage=""
                                                        onChange={(e) => setBankAccountDigit(e.target.value.replace(/\D/g, '').slice(0, 1))}
                                                        onBlur={() => {}}
                                                    />
                                                </S.AccountDigitWrapper>
                                            </S.AccountRow>
                                        </>
                                    )}
                                </>
                            )}
                        </S.FormSection>
                    </>
                )}

                <div ref={contentEndRef} />
            </S.ScrollContent>

            <S.FooterSection>
                <S.SubmitButton
                    $color={primary}
                    $disabled={isContinueDisabled()}
                    disabled={isContinueDisabled()}
                    onClick={() => { if (!hasScrolledToEnd) { scrollToBottom(); return; } handleContinue(); }}
                >
                    Continuar
                    <ChevronRightIcon width={22} height={22} />
                </S.SubmitButton>
                <PoweredByQITech />
            </S.FooterSection>

            {confirmModal.show && (
                <S.ModalOverlay
                    $closing={confirmModal.isClosing}
                    onClick={confirmModal.close}
                >
                    <S.ModalSheet
                        $closing={confirmModal.isClosing}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <S.SheetHandle />

                        {isForcedPix ? (
                            <>
                                <S.SheetTitle $color={primary}>Confirme o cadastro da sua chave PIX</S.SheetTitle>
                                <S.ConfirmRow>
                                    <S.ConfirmLabel>
                                        Chave PIX ({pixTypeLabel[pixKeyType]}):
                                    </S.ConfirmLabel>
                                    <S.ConfirmValue>{maskedPixKey()}</S.ConfirmValue>
                                </S.ConfirmRow>

                                <S.Callout>
                                    <S.CalloutIcon><Warning3Icon size={16} /></S.CalloutIcon>
                                    <S.CalloutText>
                                        Antes de continuar, verifique que a chave PIX cadastrada no seu CPF está vinculada à sua conta bancária.
                                    </S.CalloutText>
                                </S.Callout>

                                <S.SheetButtonRow>
                                    <S.SheetConfirmButton $color={primary} onClick={handleSubmit}>
                                        Confirmar
                                    </S.SheetConfirmButton>
                                    <S.SheetEditButton onClick={confirmModal.close}>
                                        Voltar
                                    </S.SheetEditButton>
                                </S.SheetButtonRow>
                            </>
                        ) : (
                            <>
                                <S.SheetTitle $color={primary}>Confirme seus dados de pagamento</S.SheetTitle>

                                {paymentMethod === 'pix_key' && (
                                    <S.ConfirmRow>
                                        <S.ConfirmLabel>
                                            Chave PIX ({pixTypeLabel[pixKeyType]}):
                                        </S.ConfirmLabel>
                                        <S.ConfirmValue>{maskedPixKey()}</S.ConfirmValue>
                                    </S.ConfirmRow>
                                )}

                                {paymentMethod === 'account' && (
                                    <>
                                        <S.ConfirmRow>
                                            <S.ConfirmLabel>Número do Banco:</S.ConfirmLabel>
                                            <S.ConfirmValue>{bankCode}</S.ConfirmValue>
                                        </S.ConfirmRow>
                                        <S.ConfirmRow>
                                            <S.ConfirmLabel>Agência:</S.ConfirmLabel>
                                            <S.ConfirmValue>{bankAgency}</S.ConfirmValue>
                                        </S.ConfirmRow>
                                        <S.ConfirmRow>
                                            <S.ConfirmLabel>Conta:</S.ConfirmLabel>
                                            <S.ConfirmValue>
                                                {bankAccount}{bankAccountDigit ? `-${bankAccountDigit}` : ''}
                                            </S.ConfirmValue>
                                        </S.ConfirmRow>
                                    </>
                                )}

                                <S.SheetButtonRow>
                                    <S.SheetConfirmButton $color={primary} onClick={handleSubmit}>
                                        Confirmar
                                    </S.SheetConfirmButton>
                                    <S.SheetEditButton onClick={confirmModal.close}>
                                        Editar
                                    </S.SheetEditButton>
                                </S.SheetButtonRow>
                            </>
                        )}
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};